import React, { useContext } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import { Metadata, mapContentTypeName } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { useSelector } from 'react-redux';
import { selectProduct, selectProductStatus } from '../../../reducers/products.selectors';
import { CurrentProductContext } from '../CurrentProductContext';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { getDateInGermanFormat } from '../../../utils/format';
import { NewAttachmentsButton } from '../../AttachmentsButton';
import Tooltip from '../../Tooltip';
import { compressClassYearArray } from '../../../utils/compressClassYearArray';
import { selectDocumentsPublishers } from '../../../reducers/publishers.selectors';
import SummaryTitle from '../../summary/SummaryTitle';
import SummaryContainer from '../../summary/SummaryContainer';
import useScreenSize from '../../../utils/useScreenSize';
import SummaryContent from '../../summary/SummaryContent';
import { Alert } from '../../NotificationAlert/NotificationAlert';
import { externalLinks } from '../../../constants';
import { ContentLicence } from '@kimuno/api-sdk/lib/types/common';

interface OwnProps {
  onShowNotification?: (alert: Alert) => void;
}

export const NewProductSummary: React.FC<OwnProps> = ({ onShowNotification }) => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const publishers = useSelector(selectDocumentsPublishers);

  const { productId } = useContext(CurrentProductContext);
  const product: Partial<Product> = useSelector((state) => selectProduct(state, productId)) || {};
  const isLoading = useSelector((state) => selectProductStatus(state, productId)) === AsyncActionStatus.LOADING;
  const publisherValue =
    publishers.find((publisher) => publisher.id === product.publisher)?.textValue || product.publisher;

  const ProductProperties = () => {
    const dateInGermanFormat =
      product.publisherReleaseDate &&
      getDateInGermanFormat(product.publisherReleaseDate);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          gap: spaces.md,
        }}
      >
        {product.subjects && product.subjects.length > 0 && (
          <Tooltip label={product.subjects.join(', ')} placement='top-start'>
            <Metadata
              icon='tag'
              sx={{ color: palette.text.secondary }}
              label={product.subjects.join(', ')}
            />
          </Tooltip>
        )}
        {product.classYears && product.classYears.length > 0 && (
          <Metadata
            icon='teacher'
            sx={{ color: palette.text.secondary }}
            label={
              compressClassYearArray(product.classYears).join(', ') +
              ' ' +
              t('Class year')
            }
          />
        )}
        {dateInGermanFormat && (
          <Metadata icon='calendar' label={dateInGermanFormat || ''} />
        )}
        {product.licence && (
          <Metadata
            icon='licence'
            label={product.licence}
            onClick={(e) => {
              e.stopPropagation();
              const link = externalLinks.licenceLinks[product.licence as ContentLicence];
              if (link) window.open(link, '_blank');
            }}
            sx={{
              color: palette.primary.main,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            role='link'
          />
        )}
      </Box>
    );
  };

  const AttachmentsButton = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        marginTop: spaces.md,
        width: isXsScreen || isSmScreen ? '100%' : 'auto',
      }}
    >
      {!!product.attachmentsCount && product.attachmentsCount > 0 && (
        <NewAttachmentsButton
          attachmentsCount={product.attachmentsCount}
          productId={productId}
          buttonSx={isXsScreen || isSmScreen ? { width: '100%' } : {}}
        />
      )}
    </Box>
  );

  return (
    <SummaryContainer>
      <SummaryTitle
        title={t('Product overview')}
        itemId={productId}
        itemData={product}
        type='product'
        onShowNotification={onShowNotification}
      />
      <SummaryContent
        isLoading={isLoading}
        itemData={product}
        primaryHeaderText={t(mapContentTypeName('product'))}
        secondaryHeaderText={publisherValue}
      >
        <ProductProperties />
        <AttachmentsButton />
      </SummaryContent>
    </SummaryContainer>
  );
};
